import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Group, Skeleton, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { APP_PREVIEW_QUERY_KEYS } from '@/lib/constants/appPreview';
import type { AppCfgsDto } from '@/lib/types/App';
import type { AppV1Cfg, AppV1Item, AppV1Row } from '@/lib/types/AppV1';
import { AppPreviewUtils } from '../AppPreviewUtils';
import { useAppPreviewBrowse, useAppPreviewCfg } from '../useAppPreview';

interface AppTvPreviewProps {
  app: AppCfgsDto;
}

/**
 * In-browser TV frame rendering the app's LIVE /app/v1 cfg + browse — the same
 * payloads the Roku shell loads (WS-2.3 app preview, transport per D-2.6).
 * It approximates the shell's home screen: real theme, real rows, real
 * artwork; hovering a card simulates remote focus.
 */
export function AppTvPreview({ app }: AppTvPreviewProps) {
  const queryClient = useQueryClient();
  const appDomain = AppPreviewUtils.appDomain(app);
  const cfgQuery = useAppPreviewCfg(appDomain);
  const browseQuery = useAppPreviewBrowse(appDomain);

  if (!appDomain) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        The preview needs your app&apos;s domain. Set one up on the Web tab first.
      </Alert>
    );
  }

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: APP_PREVIEW_QUERY_KEYS.all });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fw={600} size="lg">
            App Preview
          </Text>
          <Text size="sm" c="dimmed">
            Rendered from your app&apos;s live configuration — the same data your TV app
            loads. Recent changes can take a couple of minutes to appear.
          </Text>
        </div>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          onClick={handleRefresh}
          loading={cfgQuery.isFetching || browseQuery.isFetching}
        >
          Refresh
        </Button>
      </Group>

      {cfgQuery.isError || browseQuery.isError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          The preview could not load your app&apos;s configuration. If the app or its
          domain was just enabled, give it a couple of minutes and refresh. The app must
          be Active for its content service to answer.
        </Alert>
      ) : cfgQuery.data && browseQuery.data ? (
        <TvFrame cfg={cfgQuery.data} rows={AppPreviewUtils.visibleRows(browseQuery.data)} />
      ) : (
        <Skeleton radius="lg" style={{ aspectRatio: '16 / 9', width: '100%' }} />
      )}
    </Stack>
  );
}

// ── Internal: the frame itself. Colors/spacing come from the live theme
// payload, so everything below is inline-styled — Mantine theming must not
// leak into the simulated TV canvas. ──

interface TvFrameProps {
  cfg: AppV1Cfg;
  rows: AppV1Row[];
}

function TvFrame({ cfg, rows }: TvFrameProps) {
  const { colors, spacing, focus } = cfg.theme;
  const [focusedKey, setFocusedKey] = useState<string | null>(null);

  return (
    <div
      style={{
        aspectRatio: '16 / 9',
        width: '100%',
        maxWidth: 960,
        backgroundColor: colors.bgPrimary,
        border: '10px solid #17181c',
        borderRadius: 14,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div style={{ padding: `${spacing.safeAreaPct}%` }}>
        {/* Header: brand + nav, an approximation of the shell chrome */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}
        >
          {cfg.brand.logoUrl ? (
            <img
              src={cfg.brand.logoUrl}
              alt={cfg.brand.name}
              style={{ height: 32, maxWidth: 240, objectFit: 'contain' }}
            />
          ) : (
            <span style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700 }}>
              {cfg.brand.name}
            </span>
          )}
          <div style={{ display: 'flex', gap: 20 }}>
            {cfg.nav.map((navItem, idx) => (
              <span
                key={navItem.navId}
                style={{
                  color: idx === 0 ? colors.accent : colors.textSecondary,
                  fontSize: 14,
                  fontWeight: idx === 0 ? 600 : 400,
                }}
              >
                {navItem.title}
              </span>
            ))}
          </div>
        </div>

        {rows.length === 0 && (
          <span style={{ color: colors.textSecondary, fontSize: 14 }}>
            No content rows yet — organize media into categories to fill the home screen.
          </span>
        )}

        {rows.map((row) => (
          <div key={row.rowId} style={{ marginBottom: 24 }}>
            {row.title && (
              <div
                style={{
                  color: colors.textPrimary,
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                {row.title}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: spacing.cardGapPx / 2,
                overflowX: 'auto',
                // Room for the focus scale/ring to render without clipping.
                padding: `${focus.ringWidthPx + 4}px`,
                margin: `-${focus.ringWidthPx + 4}px`,
              }}
            >
              {row.items.map((rowItem) => (
                <TvCard
                  key={rowItem.idBC}
                  cfg={cfg}
                  row={row}
                  item={rowItem}
                  focused={focusedKey === `${row.rowId}:${rowItem.idBC}`}
                  onFocus={() => setFocusedKey(`${row.rowId}:${rowItem.idBC}`)}
                  onBlur={() => setFocusedKey(null)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface TvCardProps {
  cfg: AppV1Cfg;
  row: AppV1Row;
  item: AppV1Item;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}

function TvCard({ cfg, row, item, focused, onFocus, onBlur }: TvCardProps) {
  const { colors, focus } = cfg.theme;
  const artworkUrl = AppPreviewUtils.artworkUrl(item, row.itemAspect);
  const width = AppPreviewUtils.cardWidthPx(row.itemAspect);
  const badgeColor = item.badge === 'live' ? colors.badgeLive : colors.badgeNew;

  return (
    <div
      onMouseEnter={onFocus}
      onMouseLeave={onBlur}
      style={{ flex: '0 0 auto', width, cursor: 'default' }}
    >
      <div
        style={{
          position: 'relative',
          aspectRatio: AppPreviewUtils.aspectRatioCss(row.itemAspect),
          backgroundColor: colors.bgSurface,
          borderRadius: 6,
          overflow: 'hidden',
          outline: focused
            ? `${focus.ringWidthPx}px solid ${colors.focusRing}`
            : `1px solid ${colors.cardBorderUnfocused}`,
          transform: focused ? `scale(${focus.scale})` : 'none',
          transition: 'transform 120ms ease, outline-color 120ms ease',
        }}
      >
        {artworkUrl ? (
          <img
            src={artworkUrl}
            alt={item.title}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textSecondary,
              fontSize: 12,
              padding: 8,
              textAlign: 'center',
            }}
          >
            {item.title}
          </div>
        )}
        {item.badge && (
          <Badge
            size="xs"
            radius="sm"
            style={{
              position: 'absolute',
              top: 6,
              left: 6,
              backgroundColor: badgeColor,
              color: colors.textOnAccent,
              textTransform: 'uppercase',
            }}
          >
            {item.badge}
          </Badge>
        )}
      </div>
      {row.titleTreatment === 'below_card' && (
        <div
          style={{
            color: focused ? colors.textPrimary : colors.textSecondary,
            fontSize: 12,
            marginTop: 6,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </div>
      )}
    </div>
  );
}
